import { useState } from "react";
import { CalendarX, X, WarningCircle, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { lastFirst } from "@/lib/names";

// End one assignment; optionally close the project in the same step.
// `others` = names of OTHER workers still active on the same project.
export function EndAssignmentModal({ assignment: a, workerName, projectName, others, onClose, onDone }) {
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [completeProject, setCompleteProject] = useState(false);
  const [busy, setBusy] = useState(false);
  const inp = "mt-1 h-11 px-3 border border-slate-300 rounded-md";

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post(`/assignments/${a.id}/end`, { end_date: endDate, complete_project: completeProject });
      toast.success(`Ended ${workerName}'s assignment on "${projectName}"${data.project_completed ? ` · project marked completed` : ""}.`);
      onDone(data);
    } catch (e2) { toast.error(e2.response?.data?.detail?.toString() || e2.message); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50" data-testid="end-assignment-modal" onClick={() => !busy && onClose()}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-md w-full max-w-lg p-6 border border-slate-200 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-bold flex items-center gap-2"><CalendarX size={22} weight="duotone" className="text-slate-700" /> End assignment</h2>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={20} /></button>
        </div>
        <p className="text-sm text-slate-600"><b>{lastFirst(workerName)}</b> on <b>{projectName}</b> — the worker will no longer see this project when submitting timesheets.</p>
        <label className="block mt-4 text-xs font-bold uppercase tracking-widest text-slate-500">Last day
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="end-date-input" className={inp + " block w-48"} />
        </label>

        <div className="mt-4 border border-slate-200 rounded-md p-3 bg-slate-50 space-y-2" data-testid="end-choice">
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="radio" name="end-choice" checked={!completeProject} onChange={() => setCompleteProject(false)} data-testid="end-only-radio" className="mt-1 accent-emerald-600" />
            <span><span className="font-semibold text-slate-800">End this assignment only</span>
              <span className="block text-xs text-slate-500">Project "{projectName}" stays <b>active</b>{others.length ? ` — ${others.length} other worker${others.length === 1 ? "" : "s"} still assigned.` : " and open for new assignments."}</span></span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="radio" name="end-choice" checked={completeProject} onChange={() => setCompleteProject(true)} data-testid="end-complete-radio" className="mt-1 accent-emerald-600" />
            <span><span className="font-semibold text-slate-800">End and mark project "{projectName}" completed</span>
              {others.length === 0
                ? <span className="block text-xs text-slate-500">This is the last active worker — the project is closed out in the same step.</span>
                : <span className="block text-xs text-amber-800" data-testid="end-complete-warning">
                    <WarningCircle size={12} weight="fill" className="inline mr-1 align-[-1px]" />
                    Still assigned: {others.map(lastFirst).join(", ")}. The assignment will end, but the project <b>cannot be completed</b> until their assignments end too.
                  </span>}
            </span>
          </label>
        </div>

        <div className="mt-5 flex gap-3 justify-end">
          <button type="button" onClick={onClose} disabled={busy} className="h-11 px-4 border border-slate-300 rounded-md font-semibold">Cancel</button>
          <button data-testid="end-assignment-confirm" disabled={busy} className="h-11 px-4 bg-slate-800 hover:bg-slate-900 text-white font-semibold rounded-md disabled:opacity-60">
            {busy ? "Ending…" : (completeProject ? "End + complete project" : "End assignment")}
          </button>
        </div>
      </form>
    </div>
  );
}

// Shown after ending when the owner asked to complete the project but other workers block it.
export function ProjectNotCompletedDialog({ result: r, onClose }) {
  const ok = r.project_completed;
  return (
    <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50" data-testid="end-result-modal" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className={`bg-white rounded-md w-full max-w-md p-6 border-t-4 ${ok ? "border-emerald-600" : "border-amber-500"}`}>
        <div className="flex items-start gap-3">
          {ok ? <CheckCircle size={28} weight="fill" className="text-emerald-600 shrink-0" /> : <WarningCircle size={28} weight="fill" className="text-amber-500 shrink-0" />}
          <div className="text-sm text-slate-700">
            {ok ? (
              <><div className="font-black text-lg text-slate-900">Project marked completed</div>
                <p className="mt-1">"{r.project_name}" is now <b>completed</b>. Assignment ended {r.end_date}.</p></>
            ) : (
              <><div className="font-black text-lg text-slate-900" data-testid="project-not-completed-title">The project WAS NOT marked complete</div>
                <p className="mt-1">The assignment was ended ({r.end_date}), but <b>"{r.project_name}"</b> stays active because {r.blocked_by_workers.length === 1 ? "a worker is" : `${r.blocked_by_workers.length} workers are`} still assigned to it:</p>
                <ul className="mt-2 list-disc pl-5 font-semibold text-slate-800" data-testid="project-blockers">
                  {r.blocked_by_workers.map((n) => <li key={n}>{lastFirst(n)}</li>)}
                </ul>
                <p className="mt-2 text-xs text-slate-500">End their assignments, then mark the project completed (the last one ended offers it automatically).</p></>
            )}
          </div>
        </div>
        <button onClick={onClose} data-testid="end-result-close" className="mt-5 w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-md">Got it</button>
      </div>
    </div>
  );
}
